// src/pages/PriceQuotes/PriceQuotes.jsx

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, onSnapshot, query, orderBy, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';

// --- STYLES ---
const styles = {
    container: { fontFamily: '"Inter", sans-serif', color: '#1f2937' },
    header: {
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        marginBottom: '32px', flexWrap: 'wrap', gap: '16px'
    },
    title: { fontSize: '28px', fontWeight: '800', color: '#111827', margin: 0, letterSpacing: '-0.5px' },
    subtitle: { fontSize: '14px', color: '#6b7280', marginTop: '4px' },
    newButton: {
        padding: '12px 24px', border: 'none', borderRadius: '12px', cursor: 'pointer',
        fontSize: '14px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px',
        background: 'linear-gradient(135deg, #06C167, #764ba2)', color: 'white',
        boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)', transition: 'all 0.3s ease'
    },

    statsRow: {
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px', marginBottom: '24px'
    },
    statCard: {
        background: 'white', borderRadius: '16px', padding: '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #f1f5f9',
        display: 'flex', alignItems: 'center', gap: '16px'
    },
    statIcon: {
        width: '48px', height: '48px', borderRadius: '12px', display: 'flex',
        alignItems: 'center', justifyContent: 'center', fontSize: '22px'
    },
    statValue: { fontSize: '24px', fontWeight: '700', color: '#111827', lineHeight: 1 },
    statLabel: { fontSize: '13px', color: '#6b7280', marginTop: '4px' },

    filtersRow: {
        display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap', alignItems: 'center'
    },
    searchInput: {
        padding: '10px 16px', border: '1px solid #e2e8f0', borderRadius: '10px',
        fontSize: '14px', outline: 'none', width: '280px', backgroundColor: '#f8fafc',
        transition: 'all 0.2s'
    },
    filterSelect: {
        padding: '10px 16px', border: '1px solid #e2e8f0', borderRadius: '10px',
        fontSize: '14px', outline: 'none', backgroundColor: '#f8fafc',
        appearance: 'none', paddingRight: '36px', cursor: 'pointer',
        backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'currentColor\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6 9 12 15 18 9\'%3e%3c/polyline%3e%3c/svg%3e")',
        backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', backgroundSize: '14px'
    },

    tableContainer: {
        background: 'white', borderRadius: '16px', overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #f1f5f9'
    },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: {
        background: '#f8fafc', padding: '14px 16px', textAlign: 'left',
        borderBottom: '1px solid #e5e7eb', fontSize: '12px', fontWeight: '600',
        color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px'
    },
    td: {
        padding: '16px', borderBottom: '1px solid #f3f4f6', fontSize: '14px',
        color: '#1f2937', verticalAlign: 'middle'
    },
    tr: { transition: 'background-color 0.15s', cursor: 'pointer' },

    badge: {
        padding: '4px 12px', borderRadius: '20px', fontSize: '12px',
        fontWeight: '600', display: 'inline-block'
    },

    actionBtn: {
        padding: '6px 10px', border: '1px solid #e2e8f0', background: 'white',
        borderRadius: '8px', cursor: 'pointer', fontSize: '14px', transition: 'all 0.2s',
        marginRight: '4px'
    },

    emptyState: {
        padding: '80px 20px', textAlign: 'center', color: '#9ca3af'
    },
    emptyIcon: { fontSize: '48px', marginBottom: '16px' },
    emptyTitle: { fontSize: '18px', fontWeight: '600', color: '#6b7280', marginBottom: '8px' },
    emptyText: { fontSize: '14px', color: '#9ca3af' }
};

const statusConfig = {
    'Taslak': { bg: '#f1f5f9', color: '#475569', label: 'Taslak' },
    'Gönderildi': { bg: '#dbeafe', color: '#1d4ed8', label: 'Gönderildi' },
    'Onaylandı': { bg: '#dcfce7', color: '#06C167', label: 'Onaylandı' },
    'Reddedildi': { bg: '#fee2e2', color: '#dc2626', label: 'Reddedildi' },
};

function PriceQuotes() {
    const { userData } = useAuth();
    const navigate = useNavigate();
    const [quotes, setQuotes] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [loading, setLoading] = useState(true);

    const companyId = userData?.companyId;

    useEffect(() => {
        if (!companyId) return;
        const q = query(
            collection(db, 'companies', companyId, 'priceQuotes'),
            orderBy('createdAt', 'desc')
        );
        const unsub = onSnapshot(q, snap => {
            setQuotes(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            setLoading(false);
        });
        return () => unsub();
    }, [companyId]);

    const filteredQuotes = useMemo(() => {
        return quotes.filter(q => {
            const matchSearch = !searchTerm ||
                q.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                q.quoteNumber?.toLowerCase().includes(searchTerm.toLowerCase());
            const matchStatus = !statusFilter || q.status === statusFilter;
            return matchSearch && matchStatus;
        });
    }, [quotes, searchTerm, statusFilter]);

    const stats = useMemo(() => {
        const totalCount = quotes.length;
        const draftCount = quotes.filter(q => q.status === 'Taslak').length;
        const sentCount = quotes.filter(q => q.status === 'Gönderildi').length;
        const approvedCount = quotes.filter(q => q.status === 'Onaylandı').length;
        const totalValue = quotes.reduce((sum, q) => sum + (q.total || 0), 0);
        return { totalCount, draftCount, sentCount, approvedCount, totalValue };
    }, [quotes]);

    const formatDate = (date) => {
        if (!date) return '-';
        const d = date.seconds ? new Date(date.seconds * 1000) : new Date(date);
        return d.toLocaleDateString('tr-TR');
    };

    const formatCurrency = (amount, currency = 'TRY') => {
        if (amount == null) return '-';
        const symbol = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : '₺';
        return `${Number(amount).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${symbol}`;
    };

    const handleDelete = async (e, quoteId) => {
        e.stopPropagation();
        if (!window.confirm('Bu teklifi silmek istediğinize emin misiniz?')) return;
        try {
            await deleteDoc(doc(db, 'companies', companyId, 'priceQuotes', quoteId));
        } catch (error) {
            console.error('Teklif silinirken hata:', error);
            alert('Teklif silinirken bir hata oluştu.');
        }
    };

    if (loading) {
        return (
            <div style={styles.container}>
                <div style={{ textAlign: 'center', padding: '60px', color: '#6b7280' }}>
                    Yükleniyor...
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            {/* Header */}
            <div style={styles.header}>
                <div>
                    <h1 style={styles.title}>Fiyat Teklifleri</h1>
                    <p style={styles.subtitle}>Müşterilere gönderilen fiyat tekliflerini yönetin</p>
                </div>
                <button
                    style={styles.newButton}
                    onClick={() => navigate('/fiyat-teklifleri/yeni')}
                    onMouseEnter={e => { e.target.style.transform = 'translateY(-2px)'; e.target.style.boxShadow = '0 6px 20px rgba(102,126,234,0.4)'; }}
                    onMouseLeave={e => { e.target.style.transform = 'translateY(0)'; e.target.style.boxShadow = '0 4px 15px rgba(102,126,234,0.3)'; }}
                >
                    <span style={{ fontSize: '18px' }}>+</span> Yeni Teklif
                </button>
            </div>

            {/* Stats */}
            <div style={styles.statsRow}>
                <div style={styles.statCard}>
                    <div style={{ ...styles.statIcon, background: 'linear-gradient(135deg, #ede9fe, #ddd6fe)' }}>📋</div>
                    <div>
                        <div style={styles.statValue}>{stats.totalCount}</div>
                        <div style={styles.statLabel}>Toplam Teklif</div>
                    </div>
                </div>
                <div style={styles.statCard}>
                    <div style={{ ...styles.statIcon, background: 'linear-gradient(135deg, #e0f2fe, #bae6fd)' }}>📤</div>
                    <div>
                        <div style={styles.statValue}>{stats.sentCount}</div>
                        <div style={styles.statLabel}>Gönderildi</div>
                    </div>
                </div>
                <div style={styles.statCard}>
                    <div style={{ ...styles.statIcon, background: 'linear-gradient(135deg, #dcfce7, #bbf7d0)' }}>✅</div>
                    <div>
                        <div style={styles.statValue}>{stats.approvedCount}</div>
                        <div style={styles.statLabel}>Onaylandı</div>
                    </div>
                </div>
                <div style={styles.statCard}>
                    <div style={{ ...styles.statIcon, background: 'linear-gradient(135deg, #fef3c7, #fde68a)' }}>💰</div>
                    <div>
                        <div style={styles.statValue}>{formatCurrency(stats.totalValue)}</div>
                        <div style={styles.statLabel}>Toplam Tutar</div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div style={styles.filtersRow}>
                <input
                    type="text"
                    placeholder="🔍 Teklif no veya müşteri ara..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    style={styles.searchInput}
                />
                <select
                    value={statusFilter}
                    onChange={e => setStatusFilter(e.target.value)}
                    style={styles.filterSelect}
                >
                    <option value="">Tüm Durumlar</option>
                    <option value="Taslak">Taslak</option>
                    <option value="Gönderildi">Gönderildi</option>
                    <option value="Onaylandı">Onaylandı</option>
                    <option value="Reddedildi">Reddedildi</option>
                </select>
            </div>

            {/* Table */}
            {filteredQuotes.length === 0 ? (
                <div style={styles.emptyState}>
                    <div style={styles.emptyIcon}>📋</div>
                    <div style={styles.emptyTitle}>
                        {quotes.length === 0 ? 'Henüz teklif oluşturulmamış' : 'Arama sonucu bulunamadı'}
                    </div>
                    <div style={styles.emptyText}>
                        {quotes.length === 0
                            ? 'İlk fiyat teklifinizi oluşturmak için "Yeni Teklif" butonuna tıklayın.'
                            : 'Farklı filtreler deneyebilirsiniz.'}
                    </div>
                </div>
            ) : (
                <div style={styles.tableContainer}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Teklif No</th>
                                <th style={styles.th}>Müşteri</th>
                                <th style={styles.th}>Tarih</th>
                                <th style={styles.th}>Geçerlilik</th>
                                <th style={styles.th}>Toplam</th>
                                <th style={styles.th}>Durum</th>
                                <th style={styles.th} width="100">İşlem</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredQuotes.map(quote => {
                                const sc = statusConfig[quote.status] || statusConfig['Taslak'];
                                return (
                                    <tr
                                        key={quote.id}
                                        style={styles.tr}
                                        onClick={() => navigate(`/fiyat-teklifleri/${quote.id}`)}
                                        onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f8fafc'}
                                        onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                                    >
                                        <td style={styles.td}>
                                            <span style={{ fontWeight: '600', color: '#06C167' }}>{quote.quoteNumber || '-'}</span>
                                        </td>
                                        <td style={styles.td}>
                                            <div style={{ fontWeight: '500' }}>{quote.customerName || '-'}</div>
                                        </td>
                                        <td style={styles.td}>{formatDate(quote.quoteDate)}</td>
                                        <td style={styles.td}>{formatDate(quote.validUntil)}</td>
                                        <td style={{ ...styles.td, fontWeight: '600' }}>
                                            {formatCurrency(quote.total, quote.currency)}
                                        </td>
                                        <td style={styles.td}>
                                            <span style={{ ...styles.badge, backgroundColor: sc.bg, color: sc.color }}>
                                                {sc.label}
                                            </span>
                                        </td>
                                        <td style={styles.td}>
                                            <button
                                                style={styles.actionBtn}
                                                onClick={(e) => handleDelete(e, quote.id)}
                                                title="Sil"
                                                onMouseEnter={e => { e.target.style.borderColor = '#fca5a5'; e.target.style.color = '#ef4444'; }}
                                                onMouseLeave={e => { e.target.style.borderColor = '#e2e8f0'; e.target.style.color = '#1f2937'; }}
                                            >
                                                🗑️
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

export default PriceQuotes;
